import React, { useEffect, useState, useMemo } from 'react';
import cx from 'classnames';
import { CardImage } from 'components/common/ui/CardImage';
import type { CardImageSize } from 'components/common/ui/CardImage/types';
import ProductColorPicker from 'cards/components/common/ProductColorPicker/ProductColorPicker';
import type {
  WCardVariationColorSummaryView,
  WCardDesignFamilySummaryView,
} from '@zola/svc-web-api-ts-client';
import { useResponsiveContext } from 'components/common/ResponsiveContext';
import { Tag } from './Tag';
import { getFullSuiteColors, getFullSuiteDefaultColor } from '../utils';
import { getFullSuitePreviewImages } from './utils';
import styles from './SuiteTile.module.less';

const SUITE_IMAGE_SIZES: CardImageSize[] = [
  { width: 180, media: '(max-width: 447px)' },
  { width: 340, media: '(max-width: 767px)' },
  { width: 200, media: '(max-width: 927px)' },
];

interface SuiteTileProps {
  suite: WCardDesignFamilySummaryView;
  onClick: () => void;
  onColorChange: (color: string) => void;
  active?: boolean;
  nrfColor?: string;
}

export const SuiteTile: React.FC<SuiteTileProps> = ({
  suite,
  onClick,
  onColorChange,
  active,
  nrfColor,
}) => {
  const { isMobile } = useResponsiveContext();
  const fullSuiteImageUrlByColor = suite.fullSuiteImageUrlByColor || {};
  const colors = getFullSuiteColors(suite);
  const defaultColor = getFullSuiteDefaultColor(suite);
  const [color, setColor] = useState<string>(nrfColor || defaultColor);
  const [previewImg, setPreviewImg] = useState<string>();
  const [onPreview, setOnPreview] = useState(false);

  useEffect(() => {
    let interval = 0;

    if (onPreview) {
      let index = 0;
      const previewImages = getFullSuitePreviewImages(suite, color);
      setPreviewImg(previewImages[index]);
      interval = window.setInterval(() => {
        index = index < previewImages.length - 1 ? index + 1 : 0;
        setPreviewImg(previewImages[index]);
      }, 1000);
    }

    return () => {
      clearInterval(interval);
      setPreviewImg(undefined);
    };
  }, [onPreview]);

  const colorOptions: WCardVariationColorSummaryView[] = useMemo(
    () =>
      colors.map(c => ({
        value: c,
        label: c,
      })),
    [colors]
  );

  return (
    <div className={styles.suiteTileWrapper}>
      <div className={cx(styles.suiteTile, { [styles.suiteTileActive]: active })}>
        <div className={styles.image}>
          <CardImage
            className={styles.suiteTileImg}
            onClick={onClick}
            onMouseEnter={() => setOnPreview(true)}
            onMouseLeave={() => setOnPreview(false)}
            sourceSizes={SUITE_IMAGE_SIZES}
            defaultWidth={254}
            src={previewImg || fullSuiteImageUrlByColor[color]?.fullSuiteImageUrl}
          />
          {suite.hasStartedDraft ? <Tag>Already started</Tag> : null}
        </div>
        <div className={styles.suiteTileName}>{suite.familyDisplayName} Suite</div>
        <ProductColorPicker
          colors={colorOptions}
          activeColor={color}
          onSelect={({ value }: WCardVariationColorSummaryView) => {
            setColor(value || defaultColor);
            onColorChange(value || defaultColor);
          }}
          swatchWidth={isMobile ? 14 : 24}
        />
      </div>
    </div>
  );
};
